import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { dispatchGlobalError } from '../context/ApiErrorContext';
import { dispatchToast } from '../context/ToastContext';
import {
    Comentario,
    Post,
    deletarPost as deletarPostSvc,
} from '../services/postService';
import { useCriarCurtida, useRemoverCurtida } from '../services/curtidaService';
import { useCriarComentario, deletarComentario as deletarComentarioSvc } from '../services/comentarioService';
import { IdentidadeFeed } from '../types/identidadeFeed';
import {
    escolherIdentidadePadrao,
    resolverIdsParaMutacao,
    resolverIdExclusivoParaMutacao,
} from '../utils/identidadesFeed';
import { CurrentProfile } from '../types/profile';
import { atletasDoLado, AtletaLado } from '../utils/atletasDoLado';

interface MatchPostviewProps {
    post: Post;
    identidades?: IdentidadeFeed[];
    /** Perfil ativo derivado pela page — mesma semântica de `Postview`. */
    perfilAtivo?: CurrentProfile | null;
    readOnly?: boolean;
}

const MatchPostview: React.FC<MatchPostviewProps> = ({
    post,
    identidades,
    perfilAtivo,
    readOnly = false,
}) => {
    const auth = useAuth();
    const userObj =
        (auth?.user as any) ||
        (typeof window !== 'undefined'
            ? JSON.parse(localStorage.getItem('user') || 'null')
            : null);
    const atletaId = userObj?.atleta?.id ?? '';
    const isAdmin = userObj?.role === 'ADMIN';

    const partida = post.partida;
    const categoria = partida?.categoriaTorneio;
    const categoriaNome = categoria?.nome ?? null;
    const torneio = categoria?.torneio;
    const torneioId = torneio?.id ?? '';

    // Determinar equipe vencedora com base no placar
    const placar = partida?.placar;
    const resultadoFinalA = placar?.resultadoFinalA ?? null;
    const resultadoFinalB = placar?.resultadoFinalB ?? null;
    const temPlacar =
        resultadoFinalA !== null && resultadoFinalB !== null;

    // Sets que têm games nos DOIS lados. Um set com games nulos não tem coluna
    // para mostrar, e deixá-lo entrar viraria uma célula vazia — as duas faixas
    // deixariam de alinhar coluna a coluna, que é justamente o que este layout
    // existe para fazer.
    const setsComGames = (placar?.sets ?? []).filter(
        (s) => s.gamesA !== null && s.gamesB !== null,
    );
    const equipeAVenceu =
        temPlacar && resultadoFinalA! > resultadoFinalB!;
    const equipeBVenceu =
        temPlacar && resultadoFinalB! > resultadoFinalA!;

    // Formatação de data — campo 'data' confirmado em partida.graphqls (tipo DateTime)
    const formattedDate = partida?.data
        ? new Date(partida.data).toLocaleDateString('pt-BR', {
              year: 'numeric',
              month: 'long',
              day: 'numeric',
          })
        : null;

    // Interação: curtidas e comentários — delegado aos mesmos hooks de Postview (AC-8)
    const usarIdentidades = !!identidades && identidades.length > 0;
    const defaultIdentidade = escolherIdentidadePadrao(identidades, perfilAtivo);
    const [identidadeSelecionada] = useState<IdentidadeFeed | null>(defaultIdentidade);

    const ids = usarIdentidades && identidadeSelecionada
        ? resolverIdsParaMutacao(identidadeSelecionada)
        : { autorAtletaId: atletaId, autorTorneioId: '', autorLigaId: '' };
    const idsExclusivos = usarIdentidades && identidadeSelecionada
        ? resolverIdExclusivoParaMutacao(identidadeSelecionada)
        : { autorAtletaId: atletaId || undefined, autorTorneioId: undefined, autorLigaId: undefined };
    const canInteract = usarIdentidades
        ? !!identidadeSelecionada
        : !!(atletaId);

    const [mutCriarCurtida, { loading: liking }] = useCriarCurtida();
    const [mutRemoverCurtida, { loading: unliking }] = useRemoverCurtida();
    const [mutCriarComentario, { loading: creatingComment }] = useCriarComentario();

    const [isActive, setIsActive] = useState(post.jaCurtido ?? false);
    const [likes, setLikes] = useState<number>(post.curtidasCount ?? 0);
    const [commentCount, setCommentCount] = useState<number>(
        post.comentariosCount ?? post.comentarios?.length ?? 0
    );
    const [commentsList, setCommentsList] = useState<Comentario[]>([
        ...(post.comentarios ?? []),
    ]);
    const [newComment, setNewComment] = useState('');
    const [showComments, setShowComments] = useState(false);
    const [openCommentMenuId, setOpenCommentMenuId] = useState<string | null>(null);
    const [deletingMap, setDeletingMap] = useState<Record<string, boolean>>({});
    const [deleted, setDeleted] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [postOptionsOpen, setPostOptionsOpen] = useState(false);

    // AC-4: Ocultamento do botão Deletar para recap-posts
    // recap-posts têm autorAtleta = null (autor é o torneio) → postAtletaId = undefined.
    // A condição podeDeletar espelha exatamente a lógica de Postview.tsx:313-316:
    //   (postAtletaId && atletaId && postAtletaId === atletaId) || (torneioId && isAdmin)
    // Para recap-posts: postAtletaId é undefined → primeira cláusula sempre false.
    // Apenas ADMIN com torneioId pode deletar via UI — satisfaz AC-4.
    const postAtletaId = post.autorAtleta?.id; // undefined para recap-posts
    const podeDeletar = !!(
        (postAtletaId && atletaId && postAtletaId === atletaId) ||
        (torneioId && isAdmin)
    );

    const handleLike = async () => {
        if (readOnly || liking || unliking || !canInteract) return;
        if (!isActive) {
            try {
                await mutCriarCurtida({
                    variables: {
                        input: {
                            autorAtletaId: ids.autorAtletaId,
                            autorTorneioId: ids.autorTorneioId,
                            autorLigaId: ids.autorLigaId,
                            comentarioId: '',
                            postId: post.id,
                        },
                    },
                });
                setLikes((l) => l + 1);
                setIsActive(true);
                dispatchToast('Curtida registrada!', 'success');
            } catch {
                dispatchGlobalError('Erro ao curtir o post.');
            }
        } else {
            try {
                await mutRemoverCurtida({
                    variables: {
                        ...idsExclusivos,
                        comentarioId: '',
                        postId: post.id,
                    },
                });
                setLikes((l) => (l > 0 ? l - 1 : 0));
                setIsActive(false);
                dispatchToast('Curtida removida.', 'success');
            } catch {
                dispatchGlobalError('Erro ao remover curtida do post.');
            }
        }
    };

    const handleCreateComment = async () => {
        if (readOnly || !newComment.trim() || !canInteract) return;
        try {
            const { data } = await mutCriarComentario({
                variables: {
                    input: {
                        texto: newComment,
                        autorAtletaId: ids.autorAtletaId,
                        autorTorneioId: ids.autorTorneioId,
                        autorLigaId: ids.autorLigaId,
                        postId: post.id,
                    },
                },
            });
            const created: Comentario = data.criarComentario;
            setCommentsList((prev) => [...prev, created]);
            setCommentCount((c) => c + 1);
            setNewComment('');
            dispatchToast('Comentário publicado!', 'success');
        } catch {
            dispatchGlobalError('Erro ao criar comentário.');
        }
    };

    const handleDeleteComentario = async (comentarioId: string) => {
        if (!window.confirm('Tem certeza que deseja deletar este comentário?')) return;
        try {
            setDeletingMap((prev) => ({ ...prev, [comentarioId]: true }));
            await deletarComentarioSvc(comentarioId);
            setCommentsList((prev) => prev.filter((cm) => cm.id !== comentarioId));
            setCommentCount((c) => Math.max(0, c - 1));
        } catch {
            dispatchGlobalError('Erro ao deletar o comentário.');
        } finally {
            setDeletingMap((prev) => ({ ...prev, [comentarioId]: false }));
            setOpenCommentMenuId(null);
        }
    };

    const handleDeletePost = async () => {
        if (!podeDeletar || deleting) return;
        if (!window.confirm('Tem certeza que deseja deletar este post?')) return;
        try {
            setDeleting(true);
            await deletarPostSvc(post.id);
            setDeleted(true);
        } catch {
            dispatchGlobalError('Erro ao deletar o post.');
        } finally {
            setDeleting(false);
            setPostOptionsOpen(false);
        }
    };

    const timeAgo = (dateString: string) => {
        const diff = Date.now() - new Date(dateString).getTime();
        const hours = Math.floor(diff / 3600000);
        if (hours < 24) return 'Hoje';
        return `${Math.floor(hours / 24)}d`;
    };

    if (deleted) return null;

    // Lados da partida: usa a equipe quando presente; senão cai para os
    // participantes avulsos (rodízio — equipeA/equipeB nulos). atletasDoLado
    // aceita undefined via o tipo permissivo PartidaLadoLike.
    const atletasA: AtletaLado[] = partida ? atletasDoLado(partida, 'A') : [];
    const atletasB: AtletaLado[] = partida ? atletasDoLado(partida, 'B') : [];

    type Atleta = AtletaLado;

    // Tamanhos relativos (rem) para escalar com o tamanho da fonte raiz.
    const AVATAR_SIZE = '5.5rem';
    const BAND_HEIGHT = '3.5rem';
    // Quanto o avatar "transborda" verticalmente a faixa (acima ou abaixo)
    const AVATAR_OVERFLOW_Y = '1rem';
    // Deslocamento horizontal do segundo avatar empilhado para criar o efeito de sobreposição
    const AVATAR_INSET_X = '2.75rem';

    const renderTeamLabel = (
        atletas: Atleta[],
        venceu: boolean,
        temPlacar: boolean,
        iconTestId: string,
        side: 'left' | 'right'
    ) => {
        const iconClass = venceu
            ? 'bi bi-check-circle-fill text-success'
            : 'bi bi-x-circle-fill text-danger';

        const atletasLabel = atletas.length > 0 ? (
            atletas.map((a, idx) => (
                <React.Fragment key={a.id}>
                    {idx > 0 && <span className="text-grey-500 mx-1">•</span>}
                    {/* AC-6(g): nome clicável para perfil do atleta */}
                    <Link to={`/userpage/${a.id}`} className="text-grey-900">
                        {a.nome}
                    </Link>
                </React.Fragment>
            ))
        ) : (
            <span className="text-grey-500">Sem atletas</span>
        );

        return (
            <div
                className="d-flex align-items-center"
                style={{
                    justifyContent: side === 'left' ? 'flex-start' : 'flex-end',
                    gap: 8,
                    paddingLeft: side === 'left' ? `calc(${AVATAR_SIZE} + 0.75rem)` : 0,
                    paddingRight: side === 'right' ? `calc(${AVATAR_SIZE} + 0.75rem)` : 0,
                    fontSize: '0.95rem',
                    fontWeight: 500,
                    color: '#212529',
                    whiteSpace: 'nowrap',
                }}
            >
                {temPlacar && side === 'left' && (
                    <i className={iconClass} data-testid={iconTestId} style={{ fontSize: '1.25rem' }} />
                )}
                <span className="d-flex align-items-center">{atletasLabel}</span>
                {temPlacar && side === 'right' && (
                    <i className={iconClass} data-testid={iconTestId} style={{ fontSize: '1.25rem' }} />
                )}
            </div>
        );
    };

    // Inset lateral das faixas: começam atrás das fotos da esquerda e terminam atrás das fotos da direita.
    const BAND_INSET_X = '3rem';

    // Zona segura das faixas. As fotos são absolutas e invadem as pontas das
    // duas faixas: a mais avançada de cada lado chega a AVATAR_INSET_X +
    // AVATAR_SIZE a partir da borda do container, enquanto a faixa começa em
    // BAND_INSET_X. Com um número único centralizado isso nunca importou; com
    // uma coluna por set, a primeira e a última cairiam debaixo das fotos.
    const BAND_SAFE_X = `calc(${AVATAR_SIZE} + ${AVATAR_INSET_X} - ${BAND_INSET_X} + 0.25rem)`;

    const NUM_STYLE: React.CSSProperties = { fontSize: '1.6rem', lineHeight: 1 };

    /**
     * Faixa de uma equipe. Mostra os games de CADA set, um por coluna, alinhados
     * verticalmente com a faixa da outra equipe (`lado` diz qual metade do set
     * ler). O saldo de sets não é mais exibido — decisão do owner em 2026-09-01,
     * junto com a remoção da linha separada de games que existia abaixo das
     * faixas.
     *
     * `placar` (o resultado final em sets) sobrevive como FALLBACK: partida sem
     * sets registrados — WO, desistência, ou placar só com resultado final — não
     * tem coluna nenhuma para montar, e a faixa cai no formato anterior.
     */
    const renderBand = (
        placar: number | null,
        venceu: boolean,
        withBorder: boolean,
        lado: 'A' | 'B',
    ) => (
        <div
            className={venceu ? 'bg-success bg-opacity-10' : 'bg-light'}
            style={{
                height: BAND_HEIGHT,
                borderRadius: `calc(${BAND_HEIGHT} / 2)`,
                marginLeft: BAND_INSET_X,
                marginRight: BAND_INSET_X,
                paddingLeft: BAND_SAFE_X,
                paddingRight: BAND_SAFE_X,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: withBorder
                    ? (venceu
                        ? '2px solid rgba(25, 135, 84, 0.25)'
                        : '2px solid rgba(0, 0, 0, 0.08)')
                    : 'none',
            }}
        >
            {setsComGames.length > 0 ? (
                <div
                    data-testid={`match-band-games-${lado.toLowerCase()}`}
                    style={{
                        width: '100%',
                        display: 'grid',
                        gridTemplateColumns: `repeat(${setsComGames.length}, minmax(0, 1fr))`,
                        alignItems: 'center',
                    }}
                >
                    {setsComGames.map((set) => {
                        const games = lado === 'A' ? set.gamesA! : set.gamesB!;
                        const oponente = lado === 'A' ? set.gamesB! : set.gamesA!;
                        // Games iguais não elegem vencedor do set — mesmo critério
                        // que o `ScoreLine` usava aqui antes.
                        const venceuSet = games > oponente;
                        return (
                            <span
                                key={set.numeroSet}
                                className={venceuSet ? 'fw-700' : 'fw-600 text-grey-600'}
                                style={{
                                    ...NUM_STYLE,
                                    textAlign: 'center',
                                    // Verde escuro próprio: `text-success` do tema é
                                    // #10d876, claro demais sobre o fundo verde pálido
                                    // da faixa vencedora.
                                    ...(venceuSet ? { color: '#0f5132' } : {}),
                                }}
                            >
                                {games}
                            </span>
                        );
                    })}
                </div>
            ) : (
                <span className="fw-700 text-grey-900" style={NUM_STYLE}>
                    {placar ?? '-'}
                </span>
            )}
        </div>
    );

    const renderAvatar = (atleta: Atleta | undefined, style: React.CSSProperties) => {
        if (!atleta) return null;
        return (
            <img
                src={atleta.imagemUrl || '/assets/images/avatar.jpg'}
                alt={atleta.nome}
                className="rounded-circle border border-2 border-white shadow-sm"
                style={{
                    position: 'absolute',
                    width: AVATAR_SIZE,
                    height: AVATAR_SIZE,
                    objectFit: 'cover',
                    ...style,
                }}
            />
        );
    };

    return (
        <div className="card w-100 shadow-xss rounded-xxl border-0 p-4 mb-3">
            {/* Cabeçalho: nome do torneio como "autor" — AC-6(a) */}
            <div className="card-body p-0 d-flex align-items-center mb-3">
                <figure className="avatar me-3">
                    <img
                        src={torneio?.avatarUrl || '/assets/images/avatar.jpg'}
                        alt="avatar do torneio"
                        className="shadow-sm rounded-circle w45"
                    />
                </figure>
                <div className="flex-grow-1">
                    <h4 className="fw-700 text-grey-900 font-xssss mt-1 mb-0">
                        {torneioId ? (
                            <Link
                                to={`/tournament/${torneioId}/partidas`}
                                className="text-grey-900"
                            >
                                {torneio?.nome ?? 'Torneio'}
                            </Link>
                        ) : (
                            torneio?.nome ?? 'Torneio'
                        )}
                    </h4>
                    <span className="d-block font-xssss fw-500 mt-1 lh-3 text-grey-500">
                        {categoriaNome ? `${categoriaNome} • Recap de Partida` : 'Recap de Partida'}
                    </span>
                </div>

                {/* Opções de admin (deletar) — AC-4 */}
                {isAdmin && podeDeletar && (
                    <div className="ms-auto pointer position-relative">
                        <i
                            className="bi bi-three-dots text-grey-900 btn-round-md font-xss pointer"
                            onClick={() => setPostOptionsOpen((v) => !v)}
                        />
                        <div
                            className={`dropdown-menu dropdown-menu-end p-2 rounded-xxl border-0 shadow-lg ${postOptionsOpen ? 'show' : ''}`}
                            style={{ minWidth: 160 }}
                        >
                            <button
                                className="dropdown-item text-danger"
                                onClick={handleDeletePost}
                                disabled={deleting}
                            >
                                {deleting ? 'Deletando...' : 'Deletar Post'}
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* Faixas de equipes — AC-6(b)(c)(d)(e) — novo layout:
                - Label da equipe A (vencedora) ACIMA da primeira faixa, alinhado à esquerda.
                - Label da equipe B (perdedora) ABAIXO da segunda faixa, alinhado à direita.
                - Faixas com inset lateral (BAND_INSET_X) para acabarem atrás das fotos.
                - Faixa de cima com borda 2px levemente mais escura que o fundo.
                - 2 avatares por equipe empilhados verticalmente, com sobreposição:
                  lado esquerdo: A1 (topo, deslocado à direita) sobrepõe A2 (base, encostado na borda esquerda).
                  lado direito: B1 (topo, encostado na borda direita) sobrepõe B2 (base, deslocado à esquerda).
            */}
            <div className="card-body p-0 mb-3">
                {/* Label equipe A (topo, esquerda) */}
                {renderTeamLabel(
                    atletasA,
                    equipeAVenceu,
                    temPlacar,
                    equipeAVenceu ? 'check-icon-winner-a' : 'x-icon-loser-a',
                    'left'
                )}

                {/* Container das duas faixas + avatares posicionados absolutamente */}
                <div
                    className="position-relative"
                    style={{
                        marginTop: 6,
                        marginBottom: 6,
                    }}
                >
                    {/* Faixa equipe A (com borda) */}
                    <div>
                        {renderBand(resultadoFinalA, equipeAVenceu, true, 'A')}
                    </div>

                    {/* Faixa equipe B — encostada na faixa A (sem gap), sem borda */}
                    <div>
                        {renderBand(resultadoFinalB, equipeBVenceu, false, 'B')}
                    </div>

                    {/* Avatar 1 da equipe A — topo esquerdo, deslocado para a direita; sobrepõe avatar 2 */}
                    {renderAvatar(atletasA[0], {
                        left: AVATAR_INSET_X,
                        top: `calc(-1 * ${AVATAR_OVERFLOW_Y})`,
                        zIndex: 3,
                    })}

                    {/* Avatar 2 da equipe A — base esquerda, encostado na borda esquerda */}
                    {renderAvatar(atletasA[1], {
                        left: 0,
                        bottom: `calc(-1 * ${AVATAR_OVERFLOW_Y})`,
                        zIndex: 2,
                    })}

                    {/* Avatar 1 da equipe B:
                        - Em duplas (atletasB.length >= 2): topo direito, sobrepõe avatar 2.
                        - Em singles (atletasB.length === 1): vai para a faixa de baixo, alinhando-se
                          com o label da equipe B (que fica posicionado abaixo, à direita). */}
                    {renderAvatar(atletasB[0], {
                        right: 0,
                        ...(atletasB.length === 1
                            ? { bottom: `calc(-1 * ${AVATAR_OVERFLOW_Y})` }
                            : { top: `calc(-1 * ${AVATAR_OVERFLOW_Y})` }),
                        zIndex: 3,
                    })}

                    {/* Avatar 2 da equipe B — base direita, deslocado para a esquerda */}
                    {renderAvatar(atletasB[1], {
                        right: AVATAR_INSET_X,
                        bottom: `calc(-1 * ${AVATAR_OVERFLOW_Y})`,
                        zIndex: 2,
                    })}
                </div>


                {/* Label equipe B (base, direita) */}
                {renderTeamLabel(
                    atletasB,
                    equipeBVenceu,
                    temPlacar,
                    equipeBVenceu ? 'check-icon-winner-b' : 'x-icon-loser-b',
                    'right'
                )}
            </div>

            {/* Data e local — AC-6(f) */}
            {(formattedDate || partida?.local?.nome) && (
                <div className="card-body p-0 mb-2">
                    <p className="font-xssss text-grey-500 mb-0">
                        {formattedDate && <span>{formattedDate}</span>}
                        {formattedDate && partida?.local?.nome && <span className="mx-1">•</span>}
                        {partida?.local?.nome && <span>{partida.local.nome}</span>}
                    </p>
                </div>
            )}

            {/* A área de texto livre NÃO é renderizada (AC-6, texto = null para recap-posts) */}

            {/* Ações: curtidas e comentários — AC-8 */}
            <div className="card-body d-flex p-0">
                <div
                    className={`emoji-bttn d-flex align-items-center fw-600 text-grey-900 text-dark lh-26 font-xssss me-2 ${readOnly ? '' : 'pointer cursor-pointer'}`}
                    onClick={readOnly ? undefined : handleLike}
                    style={{
                        pointerEvents: readOnly || liking || unliking ? 'none' : undefined,
                        opacity: readOnly ? 0.5 : undefined,
                    }}
                >
                    <i
                        className={`bi bi-hand-thumbs-up me-1 btn-round-xs font-xss ${isActive ? 'bg-primary-gradiant text-white' : 'bg-white text-primary'}`}
                    />
                    {likes} Curtidas
                </div>
                <span
                    className="d-flex align-items-center fw-600 text-grey-900 text-dark lh-26 font-xssss cursor-pointer"
                    onClick={() => setShowComments((v) => !v)}
                >
                    <i className="bi bi-chat text-dark text-grey-900 btn-round-sm font-lg" />
                    <span className="d-none-xss">{commentCount} Comentários</span>
                </span>
            </div>

            {/* Seção de comentários */}
            {showComments && (
                <div className="mt-3 pt-3 border-top">
                    {!readOnly && canInteract && (
                        <div className="d-flex mb-2">
                            <input
                                type="text"
                                className="form-control"
                                placeholder="Escreva um comentário..."
                                value={newComment}
                                onChange={(e) => setNewComment(e.target.value)}
                            />
                            <button
                                className="btn btn-primary ms-2"
                                disabled={!newComment.trim() || creatingComment}
                                onClick={handleCreateComment}
                            >
                                Comentar
                            </button>
                        </div>
                    )}
                    {commentsList
                        .slice()
                        .sort(
                            (a, b) =>
                                new Date(a.criadoEm).getTime() -
                                new Date(b.criadoEm).getTime()
                        )
                        .map((c) => (
                            <div key={c.id} className="d-flex mb-2 pb-2 border-bottom">
                                <figure className="avatar me-2">
                                    <img
                                        src={
                                            c.autorAtleta?.usuario?.imagemUrl ||
                                            c.autorTorneio?.avatarUrl ||
                                            c.autorLiga?.imagemUrl ||
                                            '/assets/images/avatar.jpg'
                                        }
                                        alt="avatar"
                                        className="shadow-sm rounded-circle w35"
                                    />
                                </figure>
                                <div className="flex-grow-1">
                                    <div className="d-flex align-items-start">
                                        <div className="flex-grow-1">
                                            <h6 className="fw-700 text-grey-900 font-xssss mb-0">
                                                {c.autorAtleta?.usuario?.nome ||
                                                    c.autorTorneio?.nome ||
                                                    c.autorLiga?.nome ||
                                                    ''}
                                                <span className="d-block font-xssss fw-500 text-grey-500">
                                                    {timeAgo(c.criadoEm)}
                                                </span>
                                            </h6>
                                        </div>
                                        {((c.autorAtleta?.id ?? '') === atletaId ||
                                            (c.autorTorneio?.id && isAdmin)) && (
                                            <div className="ms-auto d-flex align-items-center">
                                                <i
                                                    className="bi bi-three-dots text-grey-900 btn-round-md font-xss pointer"
                                                    onClick={() =>
                                                        setOpenCommentMenuId(
                                                            openCommentMenuId === c.id ? null : c.id
                                                        )
                                                    }
                                                />
                                                <div
                                                    className={`dropdown-menu dropdown-menu-end p-2 rounded-xxl border-0 shadow-lg ${openCommentMenuId === c.id ? 'show' : ''}`}
                                                    style={{ minWidth: 160 }}
                                                >
                                                    <button
                                                        className="dropdown-item text-danger"
                                                        disabled={deletingMap[c.id] === true}
                                                        onClick={() => handleDeleteComentario(c.id)}
                                                    >
                                                        {deletingMap[c.id]
                                                            ? 'Deletando...'
                                                            : 'Deletar Comentário'}
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                    <p className="mb-1 font-xssss text-grey-900">{c.texto}</p>
                                </div>
                            </div>
                        ))}
                </div>
            )}
        </div>
    );
};

export default MatchPostview;
